import { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Divider, Drawer, IconButton,
  Link, Stack, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import GavelIcon from "@mui/icons-material/Gavel";
import BlockIcon from "@mui/icons-material/Block";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryIcon from "@mui/icons-material/History";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  getFichaProveedor, getHistorialProveedor,
  ProveedorDetalle, Sancion, Penalidad,
} from "../services/oece.service";
import { C, EstadoProveedor, Pill, TiposRnp, fecha, fechaHora, num } from "./oeceUi";

interface Props {
  ruc: string | null;
  onClose: () => void;
}

/* ─── Bloques ─── */
const Seccion = ({
  icono, titulo, cantidad, children,
}: { icono: React.ReactNode; titulo: string; cantidad: number; children: React.ReactNode }) => (
  <Box sx={{ mb: 3 }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
      <Box sx={{ color: C.navy2, display: "flex" }}>{icono}</Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.navy }}>{titulo}</Typography>
      <Pill tono={cantidad ? "gold" : "gray"}>{cantidad}</Pill>
    </Stack>
    {cantidad === 0 ? (
      <Typography variant="body2" sx={{ color: C.muted, pl: 4 }}>Sin registros.</Typography>
    ) : (
      <Stack spacing={1.25}>{children}</Stack>
    )}
  </Box>
);

const Dato = ({ label, valor }: { label: string; valor?: React.ReactNode }) =>
  valor ? (
    <Box>
      <Typography sx={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{valor}</Typography>
    </Box>
  ) : null;

const vacioANull = (s?: string) => (s && s !== "null" ? s : undefined);

const SancionCard = ({ s }: { s: Sancion }) => (
  <Box
    sx={{
      border: `1px solid ${s.vigente ? "#f3c4c4" : C.border}`,
      borderLeft: `4px solid ${s.vigente ? C.red : "#c5ceda"}`,
      borderRadius: 2, p: 1.5, bgcolor: s.vigente ? "#fffafa" : "#fff",
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} gap={1}>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
        {s.nroRes || s.descripcion?.slice(0, 60) || "Registro"}
      </Typography>
      {s.vigente ? <Pill tono="red">Vigente</Pill> : <Pill tono="gray">No vigente</Pill>}
    </Stack>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 1.25 }}>
      {s.nroRes && <Dato label="Tipo" valor={s.descripcion} />}
      <Dato label="Periodo" valor={s.periodo} />
      <Dato label="Resolución" valor={vacioANull(s.fechaResol)} />
      <Dato label="Inicio" valor={vacioANull(s.fechaIni) ? fecha(s.fechaIni) : undefined} />
      <Dato label="Fin" valor={vacioANull(s.fechaFin) ? fecha(s.fechaFin) : undefined} />
      <Dato label="Multa" valor={vacioANull(s.montoTexto)} />
      <Dato label="Pago" valor={vacioANull(s.indPago)} />
    </Box>
    {!s.nroRes && s.descripcion && (
      <Typography sx={{ fontSize: 13, mt: 1, color: "#334155", lineHeight: 1.5 }}>{s.descripcion}</Typography>
    )}
    {s.motivos && s.motivos.length > 0 && (
      <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, fontSize: 13, color: "#334155", lineHeight: 1.5 }}>
        {s.motivos.map((m, i) => <li key={i}>{m}</li>)}
      </Box>
    )}
    {s.resolucion && (
      <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px dashed ${C.border}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
          <Dato label="Expediente" valor={s.resolucion.expediente ?? "No figura en la resolución"} />
          {s.resolucion.url && (
            <Button
              size="small" variant="outlined" startIcon={<PictureAsPdfOutlinedIcon />}
              href={s.resolucion.url} target="_blank" rel="noopener"
              sx={{ textTransform: "none", fontWeight: 600, borderColor: C.border, color: C.navy2 }}
            >
              Ver resolución
            </Button>
          )}
        </Stack>
        {s.resolucion.tipo === "reconsideracion" && (
          <Typography sx={{ fontSize: 12.5, mt: 1, color: "#8a6a1c", bgcolor: C.goldSoft, borderRadius: 1.5, px: 1.25, py: 0.75 }}>
            Esta resolución resolvió la reconsideración contra la {s.resolucion.recurrida ?? "sanción original"}
            {s.resolucion.resultado ? ` (${s.resolucion.resultado.toLowerCase()})` : ""}. La vía administrativa
            ya se agotó: solo queda el contencioso-administrativo.
          </Typography>
        )}
      </Box>
    )}
  </Box>
);

const PenalidadCard = ({ p }: { p: Penalidad }) => (
  <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 2, p: 1.5 }}>
    <Stack direction="row" justifyContent="space-between" gap={1} sx={{ mb: 0.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{p.nomEntCont || "Entidad"}</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.red, whiteSpace: "nowrap" }}>
        {p.montoTexto}
      </Typography>
    </Stack>
    <Typography sx={{ fontSize: 12.5, color: "#334155" }}>
      {[p.descripcion, p.causal?.trim()].filter(Boolean).join(" · ")}
    </Typography>
    {p.desContProv && (
      <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.5 }} noWrap title={p.desContProv}>
        {p.desCatObj2 ? `${p.desCatObj2}: ` : ""}{p.desContProv}
      </Typography>
    )}
    <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 0.5 }}>{p.fecha}</Typography>
  </Box>
);

/* ─── Drawer ─── */
const OeceFichaDrawer = ({ ruc, onClose }: Props) => {
  const [ficha, setFicha] = useState<ProveedorDetalle | null>(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);
  const [historial, setHistorial] = useState<(ProveedorDetalle & { valido_hasta: string })[] | null>(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!ruc) return;
    setFicha(null);
    setHistorial(null);
    setError("");
    setCargando(true);
    getFichaProveedor(ruc)
      .then((r) => setFicha(r.data))
      .catch((e) => setError(e.response?.data?.message || "No se pudo cargar la ficha"))
      .finally(() => setCargando(false));
  }, [ruc]);

  const verHistorial = () => {
    if (!ruc) return;
    getHistorialProveedor(ruc).then((r) => setHistorial(r.data.versiones)).catch(() => setHistorial([]));
  };

  const copiarRuc = () => {
    if (!ruc) return;
    navigator.clipboard?.writeText(ruc);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1500);
  };

  const d = ficha?.detalle_antecedentes;

  return (
    <Drawer
      anchor="right"
      open={!!ruc}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, bgcolor: C.bg } }}
    >
      {/* Cabecera */}
      <Box sx={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 100%)`, color: "#fff", p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography sx={{ fontSize: 11, letterSpacing: 1.2, color: C.gold, fontWeight: 700 }}>
            FICHA DEL PROVEEDOR
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff", mt: -0.5, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mt: 0.5 }}>
          {ficha?.razon_social || (cargando ? "Cargando…" : "Proveedor")}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, opacity: 0.85 }}>
          <Typography sx={{ fontFamily: "monospace", fontSize: 14 }}>RUC {ruc}</Typography>
          <Tooltip title={copiado ? "Copiado" : "Copiar RUC"}>
            <IconButton size="small" onClick={copiarRuc} sx={{ color: "inherit" }}>
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        {ficha && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            <EstadoProveedor habilitado={ficha.habilitado} sancionado={ficha.antecedentes.alguna_vigente} />
            {ficha.apto_contratar ? (
              <Pill tono="green">Apto para contratar</Pill>
            ) : (
              <Pill tono="red">No apto para contratar</Pill>
            )}
            {ficha.rnp.vigente ? <Pill tono="blue">RNP vigente</Pill> : <Pill tono="gray">RNP no vigente</Pill>}
          </Stack>
        )}
      </Box>

      <Box sx={{ p: 3, overflowY: "auto" }}>
        {cargando && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: C.navy2 }} />
          </Box>
        )}
        {error && <Alert severity="warning">{error}</Alert>}

        {ficha && d && (
          <>
            {/* Resumen */}
            <Box
              sx={{
                display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1, mb: 3,
                bgcolor: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, p: 1.5,
              }}
            >
              {[
                ["Sanciones", ficha.antecedentes.sanciones],
                ["Inhab. judicial", ficha.antecedentes.inhab_judicial],
                ["Inhab. admin.", ficha.antecedentes.inhab_administrativa],
                ["Cautelares", ficha.antecedentes.medidas_cautelares],
                ["Penalidades", ficha.antecedentes.penalidades],
              ].map(([l, v]) => (
                <Box key={l as string} sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: (v as number) > 0 ? C.navy : "#b8c2d0" }}>
                    {v}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.muted, lineHeight: 1.2 }}>{l}</Typography>
                </Box>
              ))}
            </Box>

            {/* RNP + contacto */}
            <Box sx={{ bgcolor: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, p: 2, mb: 3 }}>
              <Typography sx={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, mb: 0.75 }}>
                Registros RNP vigentes
              </Typography>
              {ficha.rnp.tipos_vigentes.length ? (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {ficha.rnp.tipos_vigentes.map((t) => <Pill key={t.codigo} tono="blue">{t.nombre}</Pill>)}
                </Stack>
              ) : (
                <TiposRnp tipos={[]} />
              )}

              <Divider sx={{ my: 2 }} />

              <Typography sx={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, mb: 0.75 }}>
                Contacto declarado
              </Typography>
              {ficha.contacto.emails.length + ficha.contacto.telefonos.length === 0 ? (
                <Typography variant="body2" sx={{ color: C.muted }}>Sin datos de contacto.</Typography>
              ) : (
                <Stack spacing={0.75}>
                  {ficha.contacto.emails.map((e) => (
                    <Stack key={e} direction="row" spacing={1} alignItems="center">
                      <MailOutlineIcon sx={{ fontSize: 18, color: C.muted }} />
                      <Link href={`mailto:${e}`} underline="hover" sx={{ fontSize: 14 }}>{e}</Link>
                    </Stack>
                  ))}
                  {ficha.contacto.telefonos.map((t) => {
                    const cel = t.replace(/\D/g, "");
                    return (
                      <Stack key={t} direction="row" spacing={1} alignItems="center">
                        <PhoneIcon sx={{ fontSize: 18, color: C.muted }} />
                        <Link href={`tel:${cel}`} underline="hover" sx={{ fontSize: 14 }}>{t}</Link>
                        {/^9\d{8}$/.test(cel) && (
                          <Tooltip title="Abrir WhatsApp">
                            <IconButton size="small" href={`https://wa.me/51${cel}`} target="_blank" sx={{ color: "#25a244" }}>
                              <WhatsAppIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Seccion icono={<GavelIcon fontSize="small" />} titulo="Sanciones del Tribunal" cantidad={d.sanciones.length}>
              {[...d.sanciones]
                .sort((a, b) => Number(!!b.vigente) - Number(!!a.vigente))
                .map((s, i) => <SancionCard key={i} s={s} />)}
            </Seccion>

            <Seccion icono={<BlockIcon fontSize="small" />} titulo="Inhabilitaciones judiciales" cantidad={d.inhab_judicial.length}>
              {d.inhab_judicial.map((s, i) => <SancionCard key={i} s={s} />)}
            </Seccion>

            <Seccion icono={<BlockIcon fontSize="small" />} titulo="Inhabilitaciones administrativas" cantidad={d.inhab_administrativa.length}>
              {d.inhab_administrativa.map((s, i) => <SancionCard key={i} s={s} />)}
            </Seccion>

            <Seccion icono={<ShieldOutlinedIcon fontSize="small" />} titulo="Medidas cautelares" cantidad={d.medidas_cautelares.length}>
              {d.medidas_cautelares.map((s, i) => <SancionCard key={i} s={s} />)}
            </Seccion>

            <Seccion icono={<ReceiptLongIcon fontSize="small" />} titulo="Penalidades en contratos" cantidad={d.penalidades.length}>
              {d.penalidades.map((p, i) => <PenalidadCard key={i} p={p} />)}
            </Seccion>

            {/* Historial */}
            <Box sx={{ mb: 2 }}>
              {historial === null ? (
                <Button startIcon={<HistoryIcon />} onClick={verHistorial} sx={{ color: C.navy2 }}>
                  Ver historial de cambios
                </Button>
              ) : historial.length === 0 ? (
                <Typography variant="body2" sx={{ color: C.muted }}>
                  Sin versiones anteriores: no ha cambiado desde que se registró.
                </Typography>
              ) : (
                <Seccion icono={<HistoryIcon fontSize="small" />} titulo="Versiones anteriores" cantidad={historial.length}>
                  {historial.map((v) => (
                    <Box key={v.valido_hasta} sx={{ border: `1px solid ${C.border}`, borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                      <Typography sx={{ fontSize: 12, color: C.muted }}>Válida hasta {fechaHora(v.valido_hasta)}</Typography>
                      <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                        RNP {v.rnp.vigente ? "vigente" : "no vigente"} ({v.rnp.tipos_vigentes.map((t) => t.nombre).join(", ") || "—"}) ·{" "}
                        {v.habilitado ? "habilitado" : "no habilitado"} · {num(v.antecedentes.sanciones)} sanciones ·{" "}
                        {num(v.antecedentes.penalidades)} penalidades
                      </Typography>
                    </Box>
                  ))}
                </Seccion>
              )}
            </Box>

            <Typography sx={{ fontSize: 11.5, color: C.muted }}>
              Consultado en OECE: {fechaHora(ficha.consultado)} · Último cambio: {fechaHora(ficha.ultimo_cambio)}
            </Typography>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default OeceFichaDrawer;
